import { Sparkles } from "lucide-react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";

// Cost gate for a large "what changed" run. Replaces the native confirm() with
// an on-brand dialog: leads with the sheet count, shows the rough cost against
// the client's own OpenAI key, and makes "Run" a deliberate choice.
export function CompareConfirmDialog({
  open,
  sheets,
  estimateUsd,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  sheets: number;
  estimateUsd: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      size="md"
      title="Run this comparison?"
      // The shared Dialog stretches full-height (for big scrollable dialogs);
      // this is a small confirm, so size the card to its content and center it.
      className="self-center"
    >
      <div className="px-6 py-5">
        <div className="flex items-center gap-3.5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-ink">
            <Sparkles className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <div className="text-[1.05rem] font-semibold text-ink">
              {sheets} sheet{sheets === 1 ? "" : "s"} to compare
            </div>
            <div className="text-[0.86rem] text-ink-muted">
              The AI writes a change note for each one.
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-lg bg-canvas-2 px-4 py-3 text-[0.88rem]">
          <span className="text-ink-soft">Estimated cost · your OpenAI key</span>
          <span className="font-semibold text-ink">~${estimateUsd}</span>
        </div>

        <p className="mt-3 text-[0.8rem] leading-relaxed text-ink-faint">
          Cost scales with the number of sheets. The dashboard tracks the actual spend.
        </p>
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-rule px-6 py-3.5">
        <Button intent="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button intent="primary" onClick={onConfirm}>
          Run comparison
        </Button>
      </footer>
    </Dialog>
  );
}
